import {
  useEffect,
  useMemo,
  useState
} from "react";

import {
  collection,
  doc,
  onSnapshot,
  setDoc
} from "../access/firestore";

import { db } from "../firebase";
import { useClinic } from "../context/ClinicContext";
import { useNotifications } from "../notifications/NotificationProvider";
import { consentTemplates } from "../consentTemplates";

import {
  FileSignature,
  Plus,
  Pencil,
  Copy,
  Power,
  Save,
  X,
  FileText
} from "lucide-react";

const DEFAULT_NAMES = {
  exodoncia: "Exodoncia",
  endodoncia: "Endodoncia",
  ortodoncia: "Ortodoncia",
  implantes: "Implantes Dentales",
  operatoria: "Operatoria Dental",
  periodoncia: "Tratamientos de Periodoncia",
  odontologiaIntegral: "Odontología Integral",
  prostodoncia: "Prostodoncia",
  profilaxis: "Profilaxis Dental",
  radiografias: "Radiografías Dentales",
  anestesia: "Anestesia Local"
};

const dummyPatient = {
  nombre: "[PACIENTE]",
  apellidos: ""
};

const dummyDoctor = {
  nombre: "[DOCTOR]",
  cedula: "[CÉDULA]"
};

const getDefaultText = key => {
  const template = consentTemplates[key];

  if (!template) return "";

  try {
    return template({
      paciente: dummyPatient,
      doctor: dummyDoctor
    }).trim();
  } catch (error) {
    console.error(
      "No fue posible generar el consentimiento predeterminado:",
      error
    );
    return "";
  }
};

const fieldStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "12px 14px",
  border: "1px solid #d1d5db",
  borderRadius: 12,
  fontSize: 14,
  outline: "none",
  background: "#fff"
};

const primaryButton = {
  border: "none",
  borderRadius: 12,
  padding: "11px 15px",
  background: "#2563eb",
  color: "#fff",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: 14,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8
};

const secondaryButton = {
  border: "1px solid #d1d5db",
  borderRadius: 12,
  padding: "11px 15px",
  background: "#fff",
  color: "#111827",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: 14,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8
};

function Card({ children, style = {} }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 20,
        padding: 18,
        boxShadow: "0 2px 8px rgba(15,23,42,0.04)",
        ...style
      }}
    >
      {children}
    </div>
  );
}

function StatusBadge({ activo }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "5px 10px",
        borderRadius: 999,
        background: activo ? "#dcfce7" : "#f1f5f9",
        color: activo ? "#166534" : "#64748b",
        fontSize: 12,
        fontWeight: 700
      }}
    >
      {activo ? "Activo" : "Inactivo"}
    </span>
  );
}

export default function MobileConsentimientosSettings() {
  const { clinicId } = useClinic();
  const { notify } = useNotifications();

  const [configuraciones, setConfiguraciones] = useState([]);
  const [cargando, setCargando] = useState(true);

  const [editando, setEditando] = useState(null);

  const [form, setForm] = useState({
    nombre: "",
    descripcion: "",
    texto: ""
  });

  useEffect(() => {
    if (!clinicId) {
      setConfiguraciones([]);
      setCargando(false);
      return undefined;
    }

    setCargando(true);

    const ref = collection(
      db,
      "clinics",
      clinicId,
      "consentimientosConfiguracion"
    );

    return onSnapshot(
      ref,
      snapshot => {
        setConfiguraciones(
          snapshot.docs.map(item => ({
            id: item.id,
            ...item.data()
          }))
        );

        setCargando(false);
      },
      error => {
        console.error(error);
        setCargando(false);

        notify({
          type: "error",
          title: "Consentimientos",
          message: "No fue posible cargar la configuración."
        });
      }
    );
  }, [clinicId, notify]);

  const overrides = useMemo(() => {
    const map = {};

    configuraciones.forEach(item => {
      if (item.origen === "vexadent") {
        map[item.consentimientoId] = item;
      }
    });

    return map;
  }, [configuraciones]);

  const custom = useMemo(
    () =>
      configuraciones
        .filter(item => item.origen === "clinica")
        .sort((a, b) =>
          String(a.nombre || "").localeCompare(
            String(b.nombre || ""),
            "es"
          )
        ),
    [configuraciones]
  );

  const actualizarForm = (campo, valor) => {
    setForm(current => ({
      ...current,
      [campo]: valor
    }));
  };

  const abrirNuevo = () => {
    setEditando({
      mode: "new",
      id: null
    });

    setForm({
      nombre: "",
      descripcion: "",
      texto:
        "CONSENTIMIENTO INFORMADO\n\n" +
        "Yo, {PACIENTE}, declaro haber recibido información suficiente respecto al procedimiento explicado por {DOCTOR}.\n\n" +
        "RIESGOS Y COMPLICACIONES POSIBLES\n\n" +
        "- Especificar riesgos y complicaciones.\n\n" +
        "ALTERNATIVAS DE TRATAMIENTO\n\n" +
        "- Especificar alternativas.\n\n" +
        "Declaro haber tenido oportunidad de realizar preguntas y que mis dudas fueron aclaradas satisfactoriamente.\n\n" +
        "Autorizo de manera libre y voluntaria la realización del procedimiento."
    });
  };

  const abrirEdicion = item => {
    setEditando({
      mode: "edit",
      id: item.id
    });

    setForm({
      nombre: item.nombre || "",
      descripcion: item.descripcion || "",
      texto: item.texto || ""
    });
  };

  const personalizarDefault = key => {
    setEditando({
      mode: "new",
      id: null,
      baseConsentimientoId: key
    });

    setForm({
      nombre: `${DEFAULT_NAMES[key] || key} — Personalizado`,
      descripcion:
        "Versión personalizada por el consultorio basada en el consentimiento de VexaDent.",
      texto: getDefaultText(key)
    });
  };

  const duplicar = item => {
    setEditando({
      mode: "new",
      id: null,
      baseConsentimientoId:
        item.baseConsentimientoId || null
    });

    setForm({
      nombre: `${item.nombre || "Consentimiento"} — Copia`,
      descripcion: item.descripcion || "",
      texto: item.texto || ""
    });
  };

  const cancelarEdicion = () => {
    setEditando(null);

    setForm({
      nombre: "",
      descripcion: "",
      texto: ""
    });
  };

  const guardarCustom = async () => {
    if (!clinicId) return;

    const nombre = form.nombre.trim();
    const texto = form.texto.trim();

    if (!nombre || !texto) {
      notify({
        type: "warning",
        title: "Consentimientos",
        message: "El nombre y el contenido son obligatorios."
      });

      return;
    }

    try {
      const id =
        editando?.mode === "edit" && editando.id
          ? editando.id
          : `custom_${Date.now()}`;

      const anterior =
        editando?.mode === "edit"
          ? configuraciones.find(
              item => item.id === editando.id
            )
          : null;

      const versionAnterior =
        Number(
          String(anterior?.version || "0")
            .replace(/[^\d.]/g, "")
        ) || 0;

      const siguienteVersion =
        editando?.mode === "edit"
          ? (versionAnterior + 1).toFixed(1)
          : "1.0";

      await setDoc(
        doc(
          db,
          "clinics",
          clinicId,
          "consentimientosConfiguracion",
          id
        ),
        {
          consentimientoId: id,
          nombre,
          descripcion: form.descripcion.trim(),
          texto,
          origen: "clinica",
          activo: true,
          version: siguienteVersion,
          baseConsentimientoId:
            editando?.baseConsentimientoId ||
            anterior?.baseConsentimientoId ||
            null,
          creadoEn:
            anterior?.creadoEn ||
            new Date(),
          actualizadoEn: new Date()
        },
        {
          merge: true
        }
      );

      notify({
        type: "success",
        title: "Consentimientos",
        message:
          editando?.mode === "edit"
            ? `Consentimiento actualizado a la versión ${siguienteVersion}.`
            : "Consentimiento creado correctamente."
      });

      cancelarEdicion();
    } catch (error) {
      console.error(error);

      notify({
        type: "error",
        title: "Consentimientos",
        message: "No fue posible guardar el consentimiento."
      });
    }
  };

  const saveDefaultState = async (key, activo) => {
    if (!clinicId) return;

    try {
      await setDoc(
        doc(
          db,
          "clinics",
          clinicId,
          "consentimientosConfiguracion",
          `default_${key}`
        ),
        {
          consentimientoId: key,
          nombre: DEFAULT_NAMES[key] || key,
          origen: "vexadent",
          activo,
          version: "1.0",
          actualizadoEn: new Date()
        },
        {
          merge: true
        }
      );

      notify({
        type: "success",
        title: "Consentimientos",
        message: activo
          ? "Consentimiento activado."
          : "Consentimiento desactivado."
      });
    } catch (error) {
      console.error(error);

      notify({
        type: "error",
        title: "Consentimientos",
        message: "No fue posible guardar el cambio."
      });
    }
  };

  const toggleCustom = async item => {
    if (!clinicId) return;

    try {
      await setDoc(
        doc(
          db,
          "clinics",
          clinicId,
          "consentimientosConfiguracion",
          item.id
        ),
        {
          activo: item.activo === false,
          actualizadoEn: new Date()
        },
        {
          merge: true
        }
      );

      notify({
        type: "success",
        title: "Consentimientos",
        message:
          item.activo === false
            ? "Consentimiento activado."
            : "Consentimiento desactivado."
      });
    } catch (error) {
      console.error(error);

      notify({
        type: "error",
        title: "Consentimientos",
        message: "No fue posible cambiar el estado."
      });
    }
  };

  if (!clinicId) {
    return (
      <Card>
        <div
          style={{
            color: "#64748b",
            textAlign: "center",
            padding: 20
          }}
        >
          No se ha identificado el consultorio.
        </div>
      </Card>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
        paddingBottom: 20
      }}
    >
      <div>
        <h2
          style={{
            margin: 0,
            fontSize: 28,
            fontWeight: 700,
            color: "#0f172a"
          }}
        >
          Consentimientos
        </h2>

        <p
          style={{
            margin: "8px 0 0",
            color: "#64748b",
            lineHeight: 1.5,
            fontSize: 14
          }}
        >
          Configura los consentimientos informados
          disponibles para tu consultorio.
        </p>
      </div>

      <Card
        style={{
          background: "#eff6ff",
          border: "1px solid #bfdbfe"
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "flex-start"
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: "#dbeafe",
              color: "#2563eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0
            }}
          >
            <FileSignature size={21} />
          </div>

          <div>
            <div
              style={{
                fontWeight: 700,
                color: "#1e3a8a"
              }}
            >
              Biblioteca de consentimientos
            </div>

            <div
              style={{
                marginTop: 4,
                color: "#475569",
                fontSize: 13,
                lineHeight: 1.5
              }}
            >
              Puedes activar los consentimientos
              predeterminados de VexaDent o crear
              versiones personalizadas para tu consultorio.
            </div>
          </div>
        </div>
      </Card>

      {editando && (
        <Card
          style={{
            border: "1px solid #bfdbfe"
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              marginBottom: 18
            }}
          >
            <div>
              <h3
                style={{
                  margin: 0,
                  fontSize: 18
                }}
              >
                {editando.mode === "edit"
                  ? "Editar consentimiento"
                  : "Nuevo consentimiento"}
              </h3>

              <div
                style={{
                  marginTop: 4,
                  fontSize: 12,
                  color: "#64748b"
                }}
              >
                Los campos marcados se utilizarán al generar
                el documento.
              </div>
            </div>

            <button
              type="button"
              onClick={cancelarEdicion}
              style={{
                width: 38,
                height: 38,
                borderRadius: 11,
                border: "1px solid #e5e7eb",
                background: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <X size={19} />
            </button>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 16
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#475569",
                  marginBottom: 7
                }}
              >
                Nombre
              </label>

              <input
                value={form.nombre}
                onChange={e =>
                  actualizarForm(
                    "nombre",
                    e.target.value
                  )
                }
                style={fieldStyle}
                placeholder="Ej. Consentimiento para cirugía"
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#475569",
                  marginBottom: 7
                }}
              >
                Descripción
              </label>

              <input
                value={form.descripcion}
                onChange={e =>
                  actualizarForm(
                    "descripcion",
                    e.target.value
                  )
                }
                style={fieldStyle}
                placeholder="Descripción interna"
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#475569",
                  marginBottom: 7
                }}
              >
                Contenido
              </label>

              <textarea
                value={form.texto}
                onChange={e =>
                  actualizarForm(
                    "texto",
                    e.target.value
                  )
                }
                rows={16}
                style={{
                  ...fieldStyle,
                  resize: "vertical",
                  lineHeight: 1.65,
                  fontFamily: "inherit",
                  minHeight: 280
                }}
                placeholder="Escribe el contenido del consentimiento..."
              />

              <div
                style={{
                  marginTop: 8,
                  fontSize: 12,
                  color: "#64748b",
                  lineHeight: 1.5
                }}
              >
                Puedes utilizar:
                {" "}
                <strong>{"{PACIENTE}"}</strong>,
                {" "}
                <strong>{"{DOCTOR}"}</strong>
                {" "}
                y
                {" "}
                <strong>{"{CEDULA}"}</strong>.
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: 10,
                flexDirection: "column"
              }}
            >
              <button
                type="button"
                onClick={guardarCustom}
                style={{
                  ...primaryButton,
                  width: "100%"
                }}
              >
                <Save size={18} />
                Guardar consentimiento
              </button>

              <button
                type="button"
                onClick={cancelarEdicion}
                style={{
                  ...secondaryButton,
                  width: "100%"
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </Card>
      )}

      {!editando && (
        <button
          type="button"
          onClick={abrirNuevo}
          style={{
            ...primaryButton,
            width: "100%",
            padding: "14px 16px",
            fontSize: 15
          }}
        >
          <Plus size={19} />
          Nuevo consentimiento
        </button>
      )}

      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 6
          }}
        >
          <FileText
            size={20}
            color="#2563eb"
          />

          <h3
            style={{
              margin: 0,
              fontSize: 18
            }}
          >
            Biblioteca VexaDent
          </h3>
        </div>

        <p
          style={{
            margin: "0 0 16px",
            color: "#64748b",
            fontSize: 13,
            lineHeight: 1.5
          }}
        >
          Estos consentimientos vienen incluidos en VexaDent.
          Desactivarlos solo afecta a este consultorio.
        </p>

        {cargando ? (
          <div
            style={{
              padding: 20,
              textAlign: "center",
              color: "#64748b"
            }}
          >
            Cargando consentimientos...
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10
            }}
          >
            {Object.keys(consentTemplates).map(key => {
              const override = overrides[key];

              const activo =
                override?.activo !== false;

              return (
                <div
                  key={key}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 16,
                    padding: 14
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 10
                    }}
                  >
                    <div
                      style={{
                        minWidth: 0,
                        flex: 1
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          color: "#0f172a",
                          fontSize: 14
                        }}
                      >
                        {DEFAULT_NAMES[key] || key}
                      </div>

                      <div
                        style={{
                          marginTop: 4,
                          color: "#64748b",
                          fontSize: 12
                        }}
                      >
                        VexaDent · versión 1.0
                      </div>
                    </div>

                    <StatusBadge activo={activo} />
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 8,
                      marginTop: 12
                    }}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        personalizarDefault(key)
                      }
                      style={{
                        ...secondaryButton,
                        width: "100%",
                        padding: "10px 8px",
                        fontSize: 13
                      }}
                    >
                      <Pencil size={16} />
                      Personalizar
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        saveDefaultState(
                          key,
                          !activo
                        )
                      }
                      style={{
                        ...(activo
                          ? {
                              ...secondaryButton,
                              color: "#b91c1c"
                            }
                          : primaryButton),
                        width: "100%",
                        padding: "10px 8px",
                        fontSize: 13
                      }}
                    >
                      <Power size={16} />
                      {activo
                        ? "Desactivar"
                        : "Activar"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 6
          }}
        >
          <FileSignature
            size={20}
            color="#2563eb"
          />

          <h3
            style={{
              margin: 0,
              fontSize: 18
            }}
          >
            Consentimientos del consultorio
          </h3>
        </div>

        {custom.length === 0 ? (
          <div
            style={{
              color: "#64748b",
              fontSize: 13,
              lineHeight: 1.5,
              padding: "12px 0"
            }}
          >
            Todavía no has creado consentimientos
            personalizados.
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              marginTop: 14
            }}
          >
            {custom.map(item => (
              <div
                key={item.id}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 16,
                  padding: 14
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 10
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 14,
                        color: "#0f172a"
                      }}
                    >
                      {item.nombre}
                    </div>

                    <div
                      style={{
                        marginTop: 5,
                        color: "#64748b",
                        fontSize: 12,
                        lineHeight: 1.4
                      }}
                    >
                      {item.descripcion ||
                        "Sin descripción"}
                    </div>

                    <div
                      style={{
                        marginTop: 6,
                        color: "#64748b",
                        fontSize: 11
                      }}
                    >
                      Versión {item.version || "1.0"}
                    </div>
                  </div>

                  <StatusBadge
                    activo={
                      item.activo !== false
                    }
                  />
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "1fr 1fr",
                    gap: 8,
                    marginTop: 12
                  }}
                >
                  <button
                    type="button"
                    onClick={() =>
                      abrirEdicion(item)
                    }
                    style={{
                      ...secondaryButton,
                      width: "100%",
                      padding: "10px 8px",
                      fontSize: 13
                    }}
                  >
                    <Pencil size={16} />
                    Editar
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      duplicar(item)
                    }
                    style={{
                      ...secondaryButton,
                      width: "100%",
                      padding: "10px 8px",
                      fontSize: 13
                    }}
                  >
                    <Copy size={16} />
                    Duplicar
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      toggleCustom(item)
                    }
                    style={{
                      ...(item.activo === false
                        ? primaryButton
                        : {
                            ...secondaryButton,
                            color: "#b91c1c"
                          }),
                      width: "100%",
                      padding: "10px 8px",
                      fontSize: 13,
                      gridColumn: "1 / -1"
                    }}
                  >
                    <Power size={16} />
                    {item.activo === false
                      ? "Activar"
                      : "Desactivar"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}